import { useState, useEffect } from 'react';
import { emailService } from '../utils/emailService';
import { createClient } from '@sanity/client';
import ProtectedRoute from '../components/ProtectedRoute';

export default function EmailAdmin() {
  const [subscribers, setSubscribers] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = () => {
    setSubscribers(emailService.getSubscribers());
    setNotifications(emailService.getNotificationHistory());
  };

  const handleSyncToSanity = async () => {
    setSyncing(true);
    const localEmails = emailService.getSubscribers();
    
    if (localEmails.length === 0) {
      alert('No local emails to sync');
      setSyncing(false);
      return;
    }

    try {
      const client = createClient({
        projectId: 'x9c1zj30',
        dataset: 'production',
        token: 'skns4mrvJlYxg8n1ZfZZAGg8XYqBpqP37RfXD3b7RhdeTKXUlJxe9VjQLN6CH3g0lBTou4rhBqbk8eIH9dGWtAwNUeywK2pJxo32kr48gcDpQN9yH9mQzwbwSCZ7FyFN1kbM5Q09aim9NcJAEwXh2YIh3iJV08jF3drqRukjg1IUFwOarqiw',
        useCdn: false
      });

      let synced = 0;
      for (const subscriber of localEmails) {
        const existing = await client.fetch(
          `*[_type == "subscriber" && email == $email][0]`,
          { email: subscriber.email }
        );

        if (!existing) {
          await client.create({
            _type: 'subscriber',
            email: subscriber.email,
            subscribedAt: subscriber.date || new Date().toISOString(),
            status: 'active'
          });
          synced++;
        }
      }
      
      alert(`✅ Sync complete! ${synced} emails uploaded to Sanity.`);
    } catch (error) {
      console.error('Sync error:', error);
      alert('Sync failed. Check console for details.');
    }
    
    setSyncing(false);
  };

  const handleTestNotification = () => {
    if (!selectedPost) {
      alert('Please select a post to send notification for');
      return;
    }

    const result = emailService.notifySubscribers(selectedPost);
    if (result.sent) {
      alert(`✅ Test notification sent to ${result.count} subscribers!`);
      loadData();
    }
  };

  const handleClearData = () => {
    if (window.confirm('Are you sure you want to clear all email data?')) {
      emailService.clearAll();
      loadData();
      alert('🗑️ All email data cleared');
    }
  };

  const samplePosts = [
    {
      _id: 'test-1',
      title: 'Test Article: Advanced Threat Detection',
      slug: { current: 'test-advanced-threat-detection' },
      excerpt: 'This is a test notification for the email subscription system.',
      category: 'AI Security',
      readTime: '5 min read'
    }
  ];

  return (
    <ProtectedRoute>
      <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <h1 className="text-3xl font-black text-gray-900 mb-2">📧 Email Subscription Admin</h1>
          <p className="text-gray-600 mb-8">Manage email subscriptions and send notifications</p>

          {/* Stats */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6 text-center">
              <div className="text-3xl font-black text-blue-600">{subscribers.length}</div>
              <div className="text-blue-800 font-bold">Total Subscribers</div>
            </div>
            <div className="bg-green-50 border-2 border-green-200 rounded-xl p-6 text-center">
              <div className="text-3xl font-black text-green-600">{notifications.length}</div>
              <div className="text-green-800 font-bold">Notifications Sent</div>
            </div>
            <div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-6 text-center">
              <div className="text-3xl font-black text-purple-600">
                {notifications.reduce((sum, n) => sum + n.sentTo, 0)}
              </div>
              <div className="text-purple-800 font-bold">Total Emails Sent</div>
            </div>
          </div>

          {/* Test Notification */}
          <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-6 mb-8">
            <h2 className="text-xl font-black text-gray-900 mb-4">🧪 Send Test Notification</h2>
            <div className="flex flex-col md:flex-row gap-4">
              <select
                value={selectedPost?._id || ''}
                onChange={(e) => {
                  const post = samplePosts.find(p => p._id === e.target.value);
                  setSelectedPost(post);
                }}
                className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-yellow-500"
              >
                <option value="">Select a post to test...</option>
                {samplePosts.map(post => (
                  <option key={post._id} value={post._id}>{post.title}</option>
                ))}
              </select>
              <button
                onClick={handleTestNotification}
                disabled={!selectedPost || subscribers.length === 0}
                className="bg-yellow-500 hover:bg-yellow-600 disabled:bg-gray-300 text-white px-6 py-3 rounded-lg font-bold transition-colors"
              >
                Send Test Email
              </button>
            </div>
            {subscribers.length === 0 && (
              <p className="text-yellow-700 mt-2 text-sm">⚠️ No subscribers to send test email to</p>
            )}
          </div>

          {/* Sync Button */}
          <div className="mb-8">
            <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-6">
              <h3 className="text-lg font-bold text-yellow-800 mb-2">📤 Sync Local Emails</h3>
              <p className="text-yellow-700 mb-4 text-sm">
                Upload localStorage emails to Sanity CMS database
              </p>
              <button
                onClick={handleSyncToSanity}
                disabled={syncing}
                className="bg-yellow-500 hover:bg-yellow-600 disabled:bg-gray-300 text-white px-4 py-2 rounded-lg font-bold transition-colors text-sm"
              >
                {syncing ? '🔄 Syncing...' : '🚀 Sync to Sanity'}
              </button>
            </div>
          </div>

          {/* Subscribers List */}
          <div className="mb-8">
            <h2 className="text-xl font-black text-gray-900 mb-4">👥 Subscribers ({subscribers.length})</h2>
            {subscribers.length === 0 ? (
              <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-8 text-center">
                <div className="text-4xl mb-4">📭</div>
                <p className="text-gray-600">No subscribers yet</p>
              </div>
            ) : (
              <div className="bg-white border-2 border-gray-200 rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-4 text-left font-bold text-gray-900">Email</th>
                        <th className="px-6 py-4 text-left font-bold text-gray-900">Subscribed Date</th>
                        <th className="px-6 py-4 text-left font-bold text-gray-900">Status</th>
                        <th className="px-6 py-4 text-left font-bold text-gray-900">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subscribers.map((subscriber, index) => (
                        <tr key={subscriber.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-6 py-4 font-medium">{subscriber.email}</td>
                          <td className="px-6 py-4 text-gray-600">
                            {new Date(subscriber.date).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4">
                            <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-bold">
                              Active
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <button
                              onClick={() => {
                                if (window.confirm(`Remove ${subscriber.email} from subscribers?`)) {
                                  const updated = subscribers.filter(s => s.id !== subscriber.id);
                                  localStorage.setItem('subscribedEmails', JSON.stringify(updated));
                                  setSubscribers(updated);
                                }
                              }}
                              className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-sm font-bold"
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Notification History */}
          <div className="mb-8">
            <h2 className="text-xl font-black text-gray-900 mb-4">📨 Notification History ({notifications.length})</h2>
            {notifications.length === 0 ? (
              <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-8 text-center">
                <div className="text-4xl mb-4">📪</div>
                <p className="text-gray-600">No notifications sent yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {notifications.slice().reverse().map((notification) => (
                  <div key={notification.id} className="bg-white border-2 border-gray-200 rounded-xl p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="font-black text-gray-900">{notification.postTitle}</h3>
                        <p className="text-gray-600 text-sm">
                          Sent to {notification.sentTo} subscriber{notification.sentTo !== 1 ? 's' : ''}
                        </p>
                      </div>
                      <div className="text-right text-sm text-gray-500">
                        {new Date(notification.sentAt).toLocaleString()}
                      </div>
                    </div>
                    
                    {notification.subscribers && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-sm font-bold text-gray-700 mb-2">Recipients:</p>
                        <div className="flex flex-wrap gap-2">
                          {notification.subscribers.map((sub, index) => (
                            <span key={index} className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                              {sub.email}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      </div>
    </ProtectedRoute>
  );
}